import { Button, PageHeader, Tabs, TabsList, TabsTrigger, TOUCH } from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ReceiptText } from "lucide-react";
import { useState } from "react";
import { ApprovalsTable } from "@/components/expenses/ApprovalsTable";
import { ClaimDetailDialog } from "@/components/expenses/ClaimDetailDialog";
import { ClaimDialog } from "@/components/expenses/ClaimDialog";
import { ClaimsTable } from "@/components/expenses/ClaimsTable";
import { ExpenseBanner } from "@/components/expenses/ExpenseBanner";
import { ExpenseCards } from "@/components/expenses/ExpenseCards";
import { YearSwitcher } from "@/components/pto/YearSwitcher";
import { useCopyClaim, useExpensePending, useExpenses } from "@/hooks/use-expenses";
import { EXPENSE_PAGE_TAB_LABELS, EXPENSE_PAGE_TABS, type ExpensePageTab } from "@/lib/enums";
import { todayInZone } from "@/lib/time/dates";
import type { ExpenseDetail } from "@/schemas/expenses";

interface ExpensesSearch {
  year?: number;
  tab?: ExpensePageTab;
  expense?: "new";
  claim?: string;
}

/** The element the My expenses / To approve tabs control (the tabs have no TabsContent). */
const VIEW_ID = "expenses-view";

/**
 * The Expenses page (spec 3): cards and claims, plus To approve for department owners and the
 * backup approver. `?expense=new` opens the form (Home's Expense button); `?claim=<id>` a claim.
 */
export function ExpensesScreen() {
  const search = useSearch({ from: "/dashboard/expenses" });
  const navigate = useNavigate();
  const currentYear = Number(todayInZone(new Date()).slice(0, 4));
  const year = search.year ?? currentYear;
  const expenses = useExpenses(year);
  const pending = useExpensePending().data;
  const copy = useCopyClaim();
  const [editing, setEditing] = useState<ExpenseDetail | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const canApprove = pending?.canApprove ?? false;
  const tab: ExpensePageTab = search.tab === "approvals" && canApprove ? "approvals" : "mine";
  const toApprove = pending?.count ?? 0;

  const setSearch = (next: ExpensesSearch, replace = false) =>
    void navigate({
      to: "/dashboard/expenses",
      search: { year: search.year, tab: search.tab, claim: search.claim, ...next },
      replace,
    });
  const openClaim = (id: string) => setSearch({ claim: id });
  const formOpen = newOpen || editing !== null || search.expense === "new";

  function closeForm() {
    setNewOpen(false);
    setEditing(null);
    if (search.expense) setSearch({ expense: undefined }, true);
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title="Expenses"
        period={
          <YearSwitcher
            year={year}
            currentYear={currentYear}
            onChange={(y) => setSearch({ year: y === currentYear ? undefined : y })}
          />
        }
        actions={
          <Button className={TOUCH} onClick={() => setNewOpen(true)}>
            <ReceiptText aria-hidden="true" />
            New expense
          </Button>
        }
      />

      {tab === "mine" && (
        <ExpenseBanner count={toApprove} onReview={() => setSearch({ tab: "approvals" })} />
      )}

      {canApprove && (
        <Tabs
          value={tab}
          onValueChange={(v) =>
            setSearch({ tab: EXPENSE_PAGE_TABS.find((t) => t === v) ?? "mine" })
          }
        >
          <TabsList aria-label="Expenses view">
            {EXPENSE_PAGE_TABS.map((t) => (
              <TabsTrigger key={t} value={t} className={TOUCH} aria-controls={VIEW_ID}>
                {t === "approvals" && toApprove > 0
                  ? `${EXPENSE_PAGE_TAB_LABELS[t]} (${toApprove})`
                  : EXPENSE_PAGE_TAB_LABELS[t]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      <div id={VIEW_ID} className="flex flex-col gap-5">
        {tab === "mine" ? (
          <>
            {expenses.data && <ExpenseCards cards={expenses.data.cards} />}
            <ClaimsTable
              view={expenses.data}
              isError={expenses.isError}
              onRetry={() => void expenses.refetch()}
              onOpen={openClaim}
              onCopy={(c) => copy.mutate({ id: c.id }, { onSuccess: (out) => openClaim(out.id) })}
              copying={copy.isPending}
              onNew={() => setNewOpen(true)}
              onYear={(y) => setSearch({ year: y === currentYear ? undefined : y })}
            />
          </>
        ) : (
          <ApprovalsTable onOpen={openClaim} />
        )}
      </div>

      {search.claim && !editing && (
        <ClaimDetailDialog
          id={search.claim}
          onClose={() => setSearch({ claim: undefined }, true)}
          onEdit={setEditing}
        />
      )}
      {formOpen && (
        <ClaimDialog
          key={editing?.id ?? "new"}
          editing={editing}
          onClose={closeForm}
          onSaved={(out) => {
            closeForm();
            if (out.kind === "partial") openClaim(out.id);
          }}
        />
      )}
    </div>
  );
}
